/**
 * GuidedSetupPage — 가이드 맵 프로젝트 생성의 셋업 단계(전용 페이지).
 *
 * /project/:projectId/map-editor/guided 로 진입해 화면이 단계별로 전환되며
 * 프로젝트 이름 → 첫 층 이름 → CCTV IP 앞자리(선택) → 확인 을 입력받는다.
 * 생성은 대시보드와 같은 공용 경로(useProjectCreation)를 쓰고, 템플릿은
 * 주차장을 기본 적용한다.
 *
 * 생성 성공 후에는 다른 라우트로 navigate 하지 않는다 — guidedMapStore 를
 * 에디터 단계로 전환한 뒤, 생성된 mapId 를 로컬 state 에 담아 EditorPage 를
 * 그 자리에서 그대로 렌더링한다(EditorPage 는 라우트 :mapId 가 없을 때 이
 * prop 을 폴백으로 쓴다 — RoiWorkView 와 동일한 패턴). 그래서 주소창은
 * /map-editor/guided 그대로 유지되고, 화면은 EditorGuide 의 풀페이지 카드가
 * DXF 업로드부터 완료까지 계속 덮고 있다가 필요한 모달(레이어 매핑·조명
 * 그룹핑·PDF 매핑·관계 생성 등)만 그때그때 드러낸다.
 */
import React, { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import ProjectPageHeader from '../../components/ProjectPageHeader'
import { Map as MapIcon } from '@mui/icons-material'
import { useProjectCreation } from '../dashboard/useProjectCreation'
import EditorPage from '../editor/workers/EditorPage'
import {
  useGuidedMapStore,
  GuideProgress,
  SETUP_STEP_ORDER as SETUP_STEPS,
  type SetupStep,
} from '../../map-editor-features/guided-map'
import styles from './GuidedSetupPage.module.css'

const STEP_TITLES: Record<SetupStep, string> = {
  name: '프로젝트 이름을 입력해 주세요',
  floor: '첫 층 이름을 입력해 주세요',
  ip: 'CCTV IP 앞자리를 입력해 주세요 (선택)',
  confirm: '이대로 생성할까요?',
}

const STEP_DESCRIPTIONS: Record<SetupStep, string> = {
  name: '주차장 템플릿이 기본으로 적용됩니다.',
  floor: '예: B1 — 층은 생성 후 에디터에서 더 추가할 수 있어요.',
  ip: '예: 10.130 — DXF 변환 시 CCTV IP 자동 추출에 사용됩니다. 없으면 건너뛰세요.',
  confirm: '생성 후 에디터로 이동해 DXF 업로드부터 단계별로 안내합니다.',
}

/** 생성 후 에디터에서 이어질 단계 미리보기(사용자 기대치 설정용). */
const UPCOMING = ['DXF 업로드', '레이어 매핑', '조명 그룹핑', 'PDF 매핑', '관계 생성', '저장']

export default function GuidedSetupPage() {
  const navigate = useNavigate()
  const { projectId: siteProjectId } = useParams<{ projectId: string }>()
  const { createProject, applyTemplateToProject } = useProjectCreation(siteProjectId || '')

  const [step, setStep] = useState<SetupStep>('name')
  const [name, setName] = useState('')
  const [floorName, setFloorName] = useState('B1')
  // 대부분의 현장이 172.19 대역을 쓰므로 기본값으로 채워두되, 다른 대역이면 직접 수정 가능.
  const [cctvIpPrefix, setCctvIpPrefix] = useState('172.19')
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // 생성 성공 후 채워짐 — 값이 있으면 이 페이지 그대로 EditorPage 를 렌더링한다(navigate 없음).
  const [createdMapId, setCreatedMapId] = useState<string | null>(null)

  const stepIdx = SETUP_STEPS.indexOf(step)

  const goBack = () => {
    if (stepIdx === 0) {
      navigate(`/project/${siteProjectId}/map-editor`)
      return
    }
    setError(null)
    setStep(SETUP_STEPS[stepIdx - 1])
  }

  const goNext = () => {
    if (step === 'name' && !name.trim()) {
      setError('프로젝트 이름을 입력해 주세요.')
      return
    }
    setError(null)
    if (stepIdx < SETUP_STEPS.length - 1) setStep(SETUP_STEPS[stepIdx + 1])
  }

  const handleCreate = async () => {
    setCreating(true)
    setError(null)
    try {
      const lotId = await createProject({
        name: name.trim(),
        floorName: floorName.trim() || 'B1',
        cctvIpPrefix: cctvIpPrefix.trim() || undefined,
      })
      await applyTemplateToProject(lotId, 'parking')
      useGuidedMapStore.getState().enterEditor(name.trim())
      // navigate 하지 않는다 — 아래에서 이 페이지가 EditorPage 를 그대로 렌더링한다.
      setCreatedMapId(lotId)
    } catch (e) {
      setError(e instanceof Error ? e.message : '프로젝트 생성에 실패했습니다.')
      setCreating(false)
    }
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') goNext()
  }

  // 생성 완료 — 페이지 이동 없이 이 자리에서 곧장 에디터로 전환한다.
  // EditorGuide(가이드 에디터 단계)가 풀페이지 카드로 이어서 안내한다.
  if (createdMapId) {
    return <EditorPage projectId={siteProjectId} mapId={createdMapId} />
  }

  return (
    <div className={styles.page}>
      <ProjectPageHeader
        currentPageLabel="가이드 맵 생성"
        currentPageIcon={<MapIcon fontSize="inherit" />}
      />

      <div className={styles.content}>
        {/* 단계 카드 — step 을 key 로 리마운트해 화면 전환 애니메이션 */}
        <div key={step} className={styles.card}>
          {/* 셋업 4단계 + 에디터 가이드 7단계를 하나로 이은 공용 진행 표시. */}
          <GuideProgress current={step} />

          <h1 className={styles.title}>{STEP_TITLES[step]}</h1>
          <p className={styles.description}>{STEP_DESCRIPTIONS[step]}</p>

          {step === 'name' && (
            <input
              type="text"
              className={styles.input}
              value={name}
              autoFocus
              placeholder="예: 럭스타워 주차장"
              onChange={e => setName(e.target.value)}
              onKeyDown={onKeyDown}
            />
          )}

          {step === 'floor' && (
            <input
              type="text"
              className={styles.input}
              value={floorName}
              autoFocus
              placeholder="B1"
              onChange={e => setFloorName(e.target.value)}
              onKeyDown={onKeyDown}
            />
          )}

          {step === 'ip' && (
            <input
              type="text"
              className={styles.input}
              value={cctvIpPrefix}
              autoFocus
              placeholder="10.130 (선택)"
              onChange={e => setCctvIpPrefix(e.target.value)}
              onKeyDown={onKeyDown}
            />
          )}

          {step === 'confirm' && (
            <>
              <dl className={styles.summary}>
                <div>
                  <dt>프로젝트 이름</dt>
                  <dd>{name}</dd>
                </div>
                <div>
                  <dt>첫 층</dt>
                  <dd>{floorName.trim() || 'B1'}</dd>
                </div>
                <div>
                  <dt>템플릿</dt>
                  <dd>주차장 (기본)</dd>
                </div>
                <div>
                  <dt>CCTV IP 앞자리</dt>
                  <dd>{cctvIpPrefix.trim() || '(없음)'}</dd>
                </div>
              </dl>
              <div className={styles.upcoming}>
                다음 단계: {UPCOMING.join(' → ')}
              </div>
            </>
          )}

          {error && <p className={styles.error}>{error}</p>}

          <div className={styles.buttons}>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={goBack}
              disabled={creating}
            >
              {stepIdx === 0 ? '취소' : '이전'}
            </button>
            {step !== 'confirm' ? (
              <button type="button" className={styles.primaryButton} onClick={goNext}>
                {step === 'ip' && !cctvIpPrefix.trim() ? '건너뛰기' : '다음'}
              </button>
            ) : (
              <button
                type="button"
                className={styles.primaryButton}
                onClick={() => void handleCreate()}
                disabled={creating}
              >
                {creating ? '생성 중...' : '🧭 프로젝트 생성'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
